import { lazy, Suspense } from 'react';
import { createBrowserRouter, Outlet, ScrollRestoration } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { RouteFallback } from '@/components/layout/RouteFallback';
import { useAuth } from '@/lib/auth';

const HomePage = lazy(() =>
  import('@/pages/Home').then((module) => ({ default: module.HomePage })),
);
const MarketsPage = lazy(() =>
  import('@/pages/Markets').then((module) => ({ default: module.MarketsPage })),
);
const ScreenerPage = lazy(() =>
  import('@/pages/Screener').then((module) => ({ default: module.ScreenerPage })),
);
const DirectoryPage = lazy(() =>
  import('@/pages/Directory').then((module) => ({ default: module.DirectoryPage })),
);
const SymbolPage = lazy(() =>
  import('@/pages/Symbol').then((module) => ({ default: module.SymbolPage })),
);
const WatchlistPage = lazy(() =>
  import('@/pages/Watchlist').then((module) => ({ default: module.WatchlistPage })),
);
const AlertsPage = lazy(() =>
  import('@/pages/Alerts').then((module) => ({ default: module.AlertsPage })),
);
const PortfolioPage = lazy(() =>
  import('@/pages/Portfolio').then((module) => ({ default: module.PortfolioPage })),
);
const PricingPage = lazy(() =>
  import('@/pages/Pricing').then((module) => ({ default: module.PricingPage })),
);
const SettingsPage = lazy(() =>
  import('@/pages/Settings').then((module) => ({ default: module.SettingsPage })),
);
const DesignLabPage = lazy(() =>
  import('@/pages/DesignLab').then((module) => ({ default: module.DesignLabPage })),
);
const LoginPage = lazy(() =>
  import('@/pages/Login').then((module) => ({ default: module.LoginPage })),
);
const RegisterPage = lazy(() =>
  import('@/pages/Register').then((module) => ({ default: module.RegisterPage })),
);
const NotFoundPage = lazy(() =>
  import('@/pages/NotFound').then((module) => ({ default: module.NotFoundPage })),
);

function RootLayout() {
  return (
    <>
      <ScrollRestoration />
      <Suspense fallback={<RouteFallback />}>
        <Outlet />
      </Suspense>
    </>
  );
}

// DesignLabRoute keeps the internal showcase out of public reach: anyone
// without the admin role sees the standard 404, same as an unknown route.
function DesignLabRoute() {
  const { user, loading } = useAuth();
  if (loading) return <RouteFallback />;
  if (user?.role !== 'admin') return <NotFoundPage />;
  return <DesignLabPage />;
}

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      {
        element: <AppShell />,
        children: [
          { index: true, element: <HomePage /> },
          { path: 'markets', element: <MarketsPage /> },
          { path: 'screener', element: <ScreenerPage /> },
          { path: 'stocks', element: <DirectoryPage type="stock" /> },
          { path: 'etfs', element: <DirectoryPage type="etf" /> },
          { path: 'symbol/:symbol', element: <SymbolPage /> },
          { path: 'watchlist', element: <WatchlistPage /> },
          { path: 'alerts', element: <AlertsPage /> },
          { path: 'portfolio', element: <PortfolioPage /> },
          { path: 'pricing', element: <PricingPage /> },
          { path: 'settings', element: <SettingsPage /> },
          { path: 'design', element: <DesignLabRoute /> },
          { path: '*', element: <NotFoundPage /> },
        ],
      },
      { path: 'login', element: <LoginPage /> },
      { path: 'register', element: <RegisterPage /> },
    ],
  },
]);
